/**
 * Tumor Board Service
 * 
 * Provides API methods for tumor board management including
 * meeting scheduling, case submissions, and clinical trials.
 */

import { getAuth } from 'firebase/auth';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Get authorization headers for API requests
 */
const getAuthHeaders = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Handle API response
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || 'API request failed');
  }
  return response.json();
};

// =============================================================================
// TUMOR BOARD MEETINGS
// =============================================================================

/**
 * Get tumor board meetings
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @param {string} [options.status] - Filter by status
 * @param {string} [options.startDate] - Filter by start date
 * @param {string} [options.endDate] - Filter by end date
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Items per page
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getTumorBoardMeetings = async (facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.status) params.append('status', options.status);
  if (options.startDate) params.append('startDate', options.startDate);
  if (options.endDate) params.append('endDate', options.endDate);

  const response = await fetch(`${API_BASE}/api/oncology/tumor-boards?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Get a single tumor board meeting by ID
 * @param {string} meetingId - Meeting ID
 * @returns {Promise<Object>}
 */
export const getTumorBoardMeeting = async (meetingId) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/tumor-boards/${meetingId}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Create a tumor board meeting
 * @param {Object} meetingData - Meeting data
 * @param {string} meetingData.facilityId - Facility ID
 * @param {string} meetingData.meetingDate - Meeting date
 * @param {string} meetingData.meetingType - Meeting type (e.g., 'Breast Tumor Board')
 * @param {string} [meetingData.meetingTime] - Meeting time
 * @param {string} [meetingData.location] - Meeting location
 * @param {string} [meetingData.virtualMeetingLink] - Virtual meeting link
 * @param {string} meetingData.chairperson - Chairperson user ID
 * @param {Array} [meetingData.attendees] - List of attendees
 * @param {string} [meetingData.encounterId] - Encounter ID if meeting is linked to a specific encounter
 * @returns {Promise<Object>}
 */
export const createTumorBoardMeeting = async (meetingData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/tumor-boards`, {
    method: 'POST',
    headers,
    body: JSON.stringify(meetingData)
  });
  return handleResponse(response);
};

/**
 * Submit a case to tumor board
 * @param {string} tumorBoardId - Tumor board meeting ID
 * @param {Object} caseData - Case submission data
 * @param {string} caseData.patientId - Patient ID
 * @param {string} caseData.patientName - Patient name
 * @param {string} caseData.diagnosisId - Diagnosis ID
 * @param {string} caseData.diagnosisSummary - Brief diagnosis summary
 * @param {string} caseData.presentingPhysician - Presenting physician user ID
 * @param {Array} [caseData.questionsForBoard] - Questions for the board
 * @returns {Promise<Object>}
 */
export const submitCaseToTumorBoard = async (tumorBoardId, caseData) => {
  const headers = await getAuthHeaders();
  
  // Get current meeting data
  const meetingsResponse = await fetch(`${API_BASE}/api/oncology/tumor-boards?facilityId=${caseData.facilityId}`, {
    method: 'GET',
    headers
  });
  const meetings = await handleResponse(meetingsResponse);
  const meeting = meetings.data.find(m => m.id === tumorBoardId);
  
  if (!meeting) {
    throw new Error('Tumor board meeting not found');
  }
  
  // Add case to meeting
  const updatedCases = [...(meeting.cases || []), caseData];
  
  // Note: PUT endpoint for tumor boards would need to be added to the backend
  // For now, this demonstrates the intended API pattern
  await fetch(`${API_BASE}/api/oncology/tumor-boards/${tumorBoardId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      cases: updatedCases,
      status: 'Case Submitted'
    })
  });
  
  return { success: true, caseAdded: caseData };
};

/**
 * Add recommendation from tumor board
 * @param {string} tumorBoardId - Tumor board meeting ID
 * @param {Object} recommendationData - Recommendation data
 * @param {string} recommendationData.facilityId - Facility ID
 * @param {string} recommendationData.patientId - Patient ID
 * @param {string} recommendationData.diagnosisId - Diagnosis ID
 * @param {string} recommendationData.recommendationType - Type of recommendation
 * @param {string} recommendationData.recommendationDetails - Detailed recommendation
 * @param {boolean} recommendationData.consensusReached - Whether consensus was reached
 * @returns {Promise<Object>}
 */
export const addTumorBoardRecommendation = async (tumorBoardId, recommendationData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/tumor-boards/${tumorBoardId}/recommendations`, {
    method: 'POST',
    headers,
    body: JSON.stringify(recommendationData)
  });
  return handleResponse(response);
};

// =============================================================================
// CLINICAL TRIALS
// =============================================================================

/**
 * Get clinical trials
 * @param {string} facilityId - Facility ID
 * @param {Object} options - Query options
 * @param {string} [options.trialStatus] - Filter by trial status
 * @param {string} [options.cancerType] - Filter by cancer type
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Items per page
 * @returns {Promise<{data: Array, total: number, page: number, limit: number, hasMore: boolean}>}
 */
export const getClinicalTrials = async (facilityId, options = {}) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    page: options.page || 1,
    limit: options.limit || 20
  });
  if (options.trialStatus) params.append('trialStatus', options.trialStatus);
  if (options.cancerType) params.append('cancerType', options.cancerType);

  const response = await fetch(`${API_BASE}/api/oncology/clinical-trials?${params}`, {
    method: 'GET',
    headers
  });
  return handleResponse(response);
};

/**
 * Create a clinical trial
 * @param {Object} trialData - Trial data
 * @param {string} trialData.facilityId - Facility ID
 * @param {string} trialData.nctNumber - ClinicalTrials.gov NCT number
 * @param {string} trialData.protocolNumber - Protocol number
 * @param {string} trialData.title - Trial title
 * @param {string} trialData.phase - Trial phase
 * @param {string} trialData.sponsor - Sponsor name
 * @param {string} trialData.principalInvestigator - PI user ID
 * @param {Object} trialData.eligibilityCriteria - Eligibility criteria
 * @param {Array} trialData.cancerTypes - Applicable cancer types
 * @returns {Promise<Object>}
 */
export const createClinicalTrial = async (trialData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/clinical-trials`, {
    method: 'POST',
    headers,
    body: JSON.stringify(trialData)
  });
  return handleResponse(response);
};

/**
 * Enroll patient in clinical trial
 * @param {string} trialId - Clinical trial ID
 * @param {Object} enrollmentData - Enrollment data
 * @param {string} enrollmentData.facilityId - Facility ID
 * @param {string} enrollmentData.patientId - Patient ID
 * @param {string} enrollmentData.screeningDate - Screening date
 * @param {Array} [enrollmentData.screeningResults] - Screening results
 * @param {string} [enrollmentData.encounterId] - Encounter ID for screening/enrollment visit
 * @returns {Promise<Object>}
 */
export const enrollPatientInTrial = async (trialId, enrollmentData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/clinical-trials/${trialId}/enrollments`, {
    method: 'POST',
    headers,
    body: JSON.stringify(enrollmentData)
  });
  return handleResponse(response);
};

/**
 * Update trial enrollment status
 * @param {string} enrollmentId - Enrollment ID
 * @param {Object} updateData - Update data
 * @param {string} updateData.enrollmentStatus - New enrollment status
 * @param {string} [updateData.enrollmentDate] - Enrollment date
 * @param {string} [updateData.treatmentArm] - Assigned treatment arm
 * @returns {Promise<Object>}
 */
export const updateTrialEnrollment = async (enrollmentId, updateData) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/api/oncology/trial-enrollments/${enrollmentId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updateData)
  });
  return handleResponse(response);
};

/**
 * Search for matching clinical trials based on patient diagnosis
 * @param {string} facilityId - Facility ID
 * @param {Object} criteria - Search criteria
 * @param {string} criteria.cancerType - Cancer type
 * @param {string} [criteria.stage] - Cancer stage
 * @param {Array} [criteria.biomarkers] - Biomarker results
 * @returns {Promise<{data: Array}>}
 */
export const searchMatchingTrials = async (facilityId, criteria) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    facilityId,
    trialStatus: 'Recruiting'
  });
  if (criteria.cancerType) params.append('cancerType', criteria.cancerType);

  const response = await fetch(`${API_BASE}/api/oncology/clinical-trials?${params}`, {
    method: 'GET',
    headers
  });
  
  const result = await handleResponse(response);
  
  // Additional client-side filtering could be done here based on stage and biomarkers
  // In a production system, this would ideally be done server-side
  return result;
};

const tumorBoardService = {
  // Tumor Board
  getTumorBoardMeetings,
  getTumorBoardMeeting,
  createTumorBoardMeeting,
  submitCaseToTumorBoard,
  addTumorBoardRecommendation,
  // Clinical Trials
  getClinicalTrials,
  createClinicalTrial,
  enrollPatientInTrial,
  updateTrialEnrollment,
  searchMatchingTrials
};

export default tumorBoardService;

